import React, { useEffect, useMemo, useState } from "react";
import { Calendar, Download, TrendingUp, TrendingDown, Inbox, Award, DollarSign, Users, Package as PackageIcon, Target, ArrowUpRight, ArrowDownRight, Minus } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const PRESETS = [
  { id: "7d", label: "7 giorni", days: 7 },
  { id: "30d", label: "30 giorni", days: 30 },
  { id: "90d", label: "90 giorni", days: 90 },
  { id: "365d", label: "12 mesi", days: 365 },
];

const toISO = (d) => d.toISOString();
const fmtEUR = (n) => `€${(Number(n) || 0).toLocaleString('it-IT')}`;
const fmtDate = (iso) => new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });

const SOURCE_LABELS = {
  "landing-1": "Landing 1",
  "landing-2": "Landing 2",
  contact: "Contatti sito",
  outreach: "Outreach",
  csv_import: "Import CSV",
};

export default function CRMReports() {
  const [preset, setPreset] = useState("30d");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const range = useMemo(() => {
    if (preset === "custom" && customFrom && customTo) {
      return { from: new Date(customFrom).toISOString(), to: new Date(customTo).toISOString() };
    }
    const p = PRESETS.find((x) => x.id === preset) || PRESETS[1];
    const to = new Date();
    const from = new Date();
    from.setDate(from.getDate() - p.days);
    return { from: toISO(from), to: toISO(to) };
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    setLoading(true);
    api.get(`/crm/reports/overview?from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Errore caricamento report"))
      .finally(() => setLoading(false));
  }, [range.from, range.to]);

  const exportCsv = async (section) => {
    try {
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/crm/reports/export.csv?section=${section}&from=${encodeURIComponent(range.from)}&to=${encodeURIComponent(range.to)}`;
      const token = localStorage.getItem("perla_token");
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!resp.ok) throw new Error("export failed");
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `perla-report-${section}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
      toast.success("CSV scaricato");
    } catch { toast.error("Export fallito"); }
  };

  return (
    <div className="p-6 lg:p-10 max-w-7xl" data-testid="crm-reports-page">
      <div className="mb-8">
        <div className="text-xs font-bold uppercase tracking-widest text-[#FF5A1F]">— Analytics</div>
        <h1 className="font-display text-5xl uppercase text-[#0A0A0A] mt-2">Report</h1>
        <p className="text-[#0A0A0A]/60 mt-2 max-w-2xl">Performance vendite, redditività venditori, pacchetti più venduti e attribuzione campagne — confronto automatico con il periodo precedente.</p>
      </div>

      {/* Date range selector */}
      <div className="mb-8 flex flex-wrap items-center gap-2">
        <Calendar size={16} className="text-[#0A0A0A]/60 mr-1" />
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => setPreset(p.id)}
            data-testid={`period-${p.id}`}
            className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors ${preset === p.id ? "bg-[#0A0A0A] text-[#FAFAF7] border-[#0A0A0A]" : "border-[#0A0A0A]/20 text-[#0A0A0A]/70 hover:border-[#0A0A0A]"}`}
          >
            {p.label}
          </button>
        ))}
        <button
          onClick={() => setPreset("custom")}
          className={`px-4 py-2 text-xs font-bold uppercase tracking-widest border-2 transition-colors ${preset === "custom" ? "bg-[#FF5A1F] text-[#FAFAF7] border-[#FF5A1F]" : "border-[#0A0A0A]/20 text-[#0A0A0A]/70 hover:border-[#0A0A0A]"}`}
          data-testid="period-custom"
        >
          Personalizzato
        </button>
        {preset === "custom" && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)} className="border-2 border-[#0A0A0A] px-2 py-1.5 bg-white text-xs" data-testid="period-custom-from" />
            <span className="text-xs text-[#0A0A0A]/60">→</span>
            <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)} className="border-2 border-[#0A0A0A] px-2 py-1.5 bg-white text-xs" data-testid="period-custom-to" />
          </div>
        )}
        {data && (
          <span className="ml-3 text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">
            {fmtDate(data.period.from)} → {fmtDate(data.period.to)}
          </span>
        )}
      </div>

      {loading ? (
        <div className="text-[#0A0A0A]/50">Caricamento…</div>
      ) : !data ? (
        <div className="text-[#0A0A0A]/50">Nessun dato.</div>
      ) : (
        <>
          {/* KPI cards */}
          <section className="mb-12">
            <h2 className="text-xs font-bold uppercase tracking-widest text-[#0A0A0A]/50 mb-3">KPI generali</h2>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              <KpiCard icon={Inbox} label="Lead totali" value={data.kpi.total_leads} change={data.kpi.leads_change_pct} testId="kpi-leads" />
              <KpiCard icon={Award} label="Vinti" value={data.kpi.won} sub={`Persi: ${data.kpi.lost}`} testId="kpi-won" accent />
              <KpiCard icon={Target} label="Conversione" value={`${data.kpi.conversion_rate}%`} testId="kpi-conversion" />
              <KpiCard icon={DollarSign} label="Revenue" value={fmtEUR(data.kpi.revenue)} change={data.kpi.revenue_change_pct} testId="kpi-revenue" accent />
              <KpiCard icon={TrendingUp} label="Ticket medio" value={fmtEUR(data.kpi.avg_ticket)} sub={`Commissioni: ${fmtEUR(data.kpi.total_commissions)}`} testId="kpi-avg-ticket" />
            </div>
          </section>

          {/* Sellers leaderboard */}
          <section className="mb-12">
            <SectionHeader title="Classifica venditori" subtitle="Chi vende di più, chi converte meglio, quanto matura di commissione">
              <button onClick={() => exportCsv("sellers")} className="btn-outline-dark py-1.5 px-3 text-xs" data-testid="export-sellers">
                <Download size={12} /> Esporta CSV
              </button>
            </SectionHeader>
            {data.sellers.length === 0 ? (
              <EmptyBox>Nessun venditore con attività nel periodo.</EmptyBox>
            ) : (
              <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
                    <tr>
                      <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">#</th>
                      <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Venditore</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Lead</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Vinti</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Conv.</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Ticket ø</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Revenue</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Commissione</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.sellers.map((s) => (
                      <tr key={s.id} className="border-t border-[#0A0A0A]/10" data-testid={`report-seller-${s.id}`}>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center justify-center w-7 h-7 border-2 font-bold text-xs ${s.rank === 1 ? "border-[#FF5A1F] bg-[#FF5A1F] text-[#FAFAF7]" : "border-[#0A0A0A]"}`}>{s.rank}</span>
                        </td>
                        <td className="px-4 py-3 font-bold">{s.name}<div className="text-[10px] text-[#0A0A0A]/50 font-normal">{s.email}</div></td>
                        <td className="px-4 py-3 text-right">{s.leads}</td>
                        <td className="px-4 py-3 text-right font-bold">{s.won}</td>
                        <td className="px-4 py-3 text-right">{s.conversion_rate}%</td>
                        <td className="px-4 py-3 text-right">{s.won ? fmtEUR(s.avg_ticket) : "—"}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#0A0A0A]">{fmtEUR(s.revenue)}</td>
                        <td className="px-4 py-3 text-right font-bold text-[#FF5A1F]">
                          {fmtEUR(s.commission_amount)}
                          <div className="text-[10px] text-[#0A0A0A]/40 font-normal">({s.commission_pct}%)</div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Packages ranking */}
          <section className="mb-12">
            <SectionHeader title="Pacchetti più venduti" subtitle="Ranking per vendite chiuse nel periodo. In fondo i 'cadaveri' mai venduti nel periodo.">
              <button onClick={() => exportCsv("packages")} className="btn-outline-dark py-1.5 px-3 text-xs" data-testid="export-packages">
                <Download size={12} /> Esporta CSV
              </button>
            </SectionHeader>
            {data.packages.length === 0 ? (
              <EmptyBox>Nessun pacchetto in DB.</EmptyBox>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {data.packages.map((p, idx) => {
                  const isTop = idx === 0 && p.sold_count > 0;
                  const isDead = p.sold_count === 0;
                  return (
                    <div key={p.id} className={`border-2 p-5 ${isTop ? "border-[#FF5A1F] bg-[#FF5A1F]/5" : isDead ? "border-[#0A0A0A]/20 bg-[#0A0A0A]/[0.03]" : "border-[#0A0A0A] bg-[#FAFAF7]"}`} data-testid={`report-package-${p.id}`}>
                      <div className="flex items-start justify-between">
                        <PackageIcon size={20} className={isTop ? "text-[#FF5A1F]" : isDead ? "text-[#0A0A0A]/30" : "text-[#0A0A0A]"} strokeWidth={1.5} />
                        {isTop && <span className="text-[9px] uppercase tracking-widest font-bold bg-[#FF5A1F] text-[#FAFAF7] px-1.5 py-0.5">Top seller</span>}
                        {isDead && <span className="text-[9px] uppercase tracking-widest font-bold text-[#0A0A0A]/40">Mai venduto</span>}
                      </div>
                      <h3 className={`mt-3 font-bold text-lg ${isDead ? "text-[#0A0A0A]/50" : "text-[#0A0A0A]"}`}>{p.name}</h3>
                      <div className="mt-1 text-xs text-[#0A0A0A]/50">Prezzo listino: {fmtEUR(p.price)}</div>
                      <div className="mt-4 flex items-baseline justify-between">
                        <div>
                          <div className="font-display text-4xl leading-none">{p.sold_count}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 mt-1">vendite</div>
                        </div>
                        <div className="text-right">
                          <div className="font-display text-2xl text-[#FF5A1F] leading-none">{fmtEUR(p.revenue)}</div>
                          <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 mt-1">revenue</div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          {/* Attribution */}
          <section className="mb-12">
            <SectionHeader title="Attribuzione campagne" subtitle="Da quali campagne UTM arriva il fatturato (top 50 per revenue)">
              <button onClick={() => exportCsv("attribution")} className="btn-outline-dark py-1.5 px-3 text-xs" data-testid="export-attribution">
                <Download size={12} /> Esporta CSV
              </button>
            </SectionHeader>
            {data.attribution.length === 0 ? (
              <EmptyBox>Nessuna campagna tracciata nel periodo. I lead con parametri UTM appariranno qui.</EmptyBox>
            ) : (
              <div className="border-2 border-[#0A0A0A] bg-[#FAFAF7] overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-[#0A0A0A] text-[#FAFAF7]">
                    <tr>
                      <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Campagna</th>
                      <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Fonte</th>
                      <th className="text-left px-4 py-3 uppercase text-xs tracking-widest">Piatt.</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Lead</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Vinti</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Conv.</th>
                      <th className="text-right px-4 py-3 uppercase text-xs tracking-widest">Revenue</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.attribution.map((a, i) => (
                      <tr key={i} className="border-t border-[#0A0A0A]/10">
                        <td className="px-4 py-3 font-mono text-xs">{a.campaign}</td>
                        <td className="px-4 py-3 text-xs">{a.source}</td>
                        <td className="px-4 py-3 text-xs uppercase">{a.platform}</td>
                        <td className="px-4 py-3 text-right">{a.leads}</td>
                        <td className="px-4 py-3 text-right font-bold">{a.won}</td>
                        <td className="px-4 py-3 text-right">{a.conversion_rate}%</td>
                        <td className="px-4 py-3 text-right font-bold text-[#FF5A1F]">{fmtEUR(a.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Sources by page */}
          <section className="mb-6">
            <SectionHeader title="Pagine d'ingresso" subtitle="Da quale pagina/canale sono entrati i lead">{null}</SectionHeader>
            {data.sources_by_page.length === 0 ? (
              <EmptyBox>Nessuna pagina d'ingresso registrata nel periodo.</EmptyBox>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                {data.sources_by_page.map((s) => (
                  <div key={s.source} className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={`report-source-${s.source}`}>
                    <div className="text-[10px] uppercase tracking-widest font-bold text-[#FF5A1F]">{SOURCE_LABELS[s.source] || s.source}</div>
                    <div className="mt-3 font-display text-4xl leading-none">{s.leads}</div>
                    <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/60 mt-1">lead</div>
                    <div className="mt-4 pt-3 border-t border-[#0A0A0A]/10 grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Vinti</div>
                        <div className="font-bold">{s.won}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Conv.</div>
                        <div className="font-bold">{s.conversion_rate}%</div>
                      </div>
                      <div className="col-span-2">
                        <div className="text-[10px] uppercase tracking-widest text-[#0A0A0A]/50">Revenue</div>
                        <div className="font-bold text-[#FF5A1F]">{fmtEUR(s.revenue)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

const KpiCard = ({ icon: Icon, label, value, sub, change, accent, testId }) => {
  const positive = change !== null && change !== undefined && change > 0;
  const negative = change !== null && change !== undefined && change < 0;
  const ChangeIcon = positive ? ArrowUpRight : negative ? ArrowDownRight : Minus;
  return (
    <div className="border-2 border-[#0A0A0A] p-5 bg-[#FAFAF7]" data-testid={testId}>
      <Icon size={20} strokeWidth={1.5} className={accent ? "text-[#FF5A1F]" : "text-[#0A0A0A]"} />
      <div className="mt-3 font-display text-3xl leading-none text-[#0A0A0A]">{value}</div>
      <div className="mt-2 text-[10px] uppercase tracking-widest text-[#0A0A0A]/60">{label}</div>
      {(change !== null && change !== undefined) && (
        <div className={`mt-2 text-[10px] uppercase tracking-widest font-bold flex items-center gap-0.5 ${positive ? "text-emerald-700" : negative ? "text-red-700" : "text-[#0A0A0A]/40"}`}>
          <ChangeIcon size={11} /> {change > 0 ? "+" : ""}{change}% vs prec.
        </div>
      )}
      {sub && <div className="mt-1 text-[10px] text-[#0A0A0A]/50">{sub}</div>}
    </div>
  );
};

const SectionHeader = ({ title, subtitle, children }) => (
  <div className="mb-4 flex items-baseline justify-between gap-4 flex-wrap">
    <div>
      <h2 className="font-display text-3xl uppercase text-[#0A0A0A]">{title}</h2>
      {subtitle && <p className="text-xs text-[#0A0A0A]/60 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

const EmptyBox = ({ children }) => (
  <div className="border-2 border-dashed border-[#0A0A0A]/20 p-8 text-center text-[#0A0A0A]/50 text-sm">{children}</div>
);
